import type {
  CommandeFournisseurDetail,
  ReceptionFournisseurPayload,
  ReceptionFournisseurResponse,
} from "@/types/domain";

export type LigneReception = CommandeFournisseurDetail["lignes"][number];

export function creerCleReception(
  ligneId: string,
  horodatage = Date.now(),
  alea = Math.random().toString(36).slice(2, 10),
): string {
  const ligneNormalisee = ligneId.replace(/[^A-Za-z0-9_-]/g, "-");
  return `RF04:${ligneNormalisee}:${horodatage}:${alea}`;
}

export function construireReception(
  ligne: LigneReception,
  quantite: number,
  idempotencyKey: string,
  numeroReception?: string,
): ReceptionFournisseurPayload {
  if (!Number.isInteger(quantite) || quantite <= 0) {
    throw new Error("La quantité reçue doit être un entier supérieur à zéro.");
  }
  if (!["COMMANDE", "PARTIELLEMENT_RECU"].includes(ligne.statut)) {
    throw new Error("Cette ligne n’est plus recevable.");
  }
  if (quantite > ligne.quantite_restante) {
    throw new Error(
      `La quantité reçue dépasse le reliquat affiché (${ligne.quantite_restante}).`,
    );
  }
  if (!idempotencyKey.trim()) {
    throw new Error("La clé idempotente de réception est requise.");
  }
  return {
    idempotency_key: idempotencyKey.trim(),
    quantite_recue: quantite,
    numero_reception: numeroReception?.trim() || null,
  };
}

interface ExecuterReceptionOptions {
  commandeId: string;
  ligneId: string;
  payload: ReceptionFournisseurPayload;
  recevoir: (
    commandeId: string,
    ligneId: string,
    payload: ReceptionFournisseurPayload,
  ) => Promise<ReceptionFournisseurResponse>;
  rafraichir: () => Promise<void>;
  verrouiller: (valeur: boolean) => void;
}

export async function executerReception({
  commandeId,
  ligneId,
  payload,
  recevoir,
  rafraichir,
  verrouiller,
}: ExecuterReceptionOptions): Promise<ReceptionFournisseurResponse> {
  verrouiller(true);
  try {
    const resultat = await recevoir(commandeId, ligneId, payload);
    await rafraichir();
    return resultat;
  } finally {
    verrouiller(false);
  }
}
